import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

// Actions
import { parseFetchOptions } from "../state/user/actions";

// Config
import { getOrganizationsUrl } from "../config/apiConfig";

// Hooks
import { useAppInsights } from "../utils/telemetry/AppInsights";

const OrganizationsContext = createContext();

export const useOrganizations = () => {
  const context = useContext(OrganizationsContext);

  if (!context) {
    throw new Error(
      "useOrganizations must be used inside OrganizationsProvider"
    );
  }

  useEffect(() => {
    // Lazy-loaded
    // Data is fetched only when a component consumes useOrganizations()
    context.loadOrganizations();
  }, []);

  return context;
};

const OrganizationsProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const { trackException } = useAppInsights();

  const loadOrganizations = useCallback(() => {
    if (loading || organizations.length > 0) return;

    setLoading(true);

    dispatch(
      parseFetchOptions(({ headers }) => {
        fetch(getOrganizationsUrl, { headers })
          .then(res => res.json())
          .then(result => {
            setOrganizations(result);
          })
          .catch(() => {
            trackException(`Failed to get organizations`);
          })
          .finally(() => {
            setLoading(false);
          });
      })
    );
  }, [dispatch, loading, organizations.length, trackException]);

  return (
    <OrganizationsContext.Provider
      value={{ organizations, loading, loadOrganizations }}
    >
      {children}
    </OrganizationsContext.Provider>
  );
};

OrganizationsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default OrganizationsProvider;
